import type { TypedStartListening } from "@reduxjs/toolkit";
import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "../../../common/state/store";
import { askPublication } from "../../builds/state/buildsSlice";
import {
  continuePlanModeChat,
  generateFromPlanThunk,
  startPlanModeChat,
} from "../../chatbot/state/chatbotSlice";
import {
  duplicateProjectGroup,
  postProjectGroup,
} from "../../projectGroups/state/projectGroupsSlice";
import {
  createNewScene,
  duplicateProject,
  setProjectTeam,
} from "../../projects/state/projectsSlice";
import { interactionCreatedLocally } from "../../graph/state/graphLegacy";
import { setInteriorSceneStack } from "../../graph/state/graphViewSlice";
import { selectHasDemoSessions } from "../../stats/state/statsSlice";
import {
  bulkInviteEndusersWithoutProject,
  inviteCreatorWithPerms,
  inviteEnduserWithPerms,
  inviteExternalCreatorToOrg,
  inviteExternalEnduser,
  inviteExternalEnduserWithoutEmail,
  inviteExternalEnduserWithoutProject,
} from "../../team/state/teamSlice";
import { enqueueOnboardingEvent } from "../events/eventQueue";
import { getChecklistForAudience } from "../registry";
import {
  applyOnboardingOps,
  awaitStepInterrupted,
  checklistItemCredited,
  getChecklistProgress,
  openChecklistCompletePrompt,
  openDemoCleanupPrompt,
  tourSignal,
  selectOnboardingAudience,
  selectOnboardingRemote,
  selectSuggestionsMuted,
} from "./onboardingSlice";

export const onboardingListener = createListenerMiddleware();

export const startAppListening = onboardingListener.startListening as TypedStartListening<
  RootState,
  AppDispatch
>;

type MarkItemApi = {
  dispatch: AppDispatch;
  getState: () => RootState;
};

export const markItem = (api: MarkItemApi, itemId: string): void => {
  api.dispatch(checklistItemCredited(itemId));
  const state = api.getState();
  const audience = selectOnboardingAudience(state);
  const remote = selectOnboardingRemote(state);
  if (!audience || !remote) {
    return;
  }
  const checklist = getChecklistForAudience(audience);
  if (!checklist || !checklist.items.some((item) => item.id === itemId)) {
    return;
  }
  if (remote.checklist.items[itemId]) {
    return;
  }
  void api.dispatch(applyOnboardingOps([{ op: "checklistItemDone", id: itemId }]));
  enqueueOnboardingEvent({
    event: "checklist_item_done",
    surface: "checklist",
    itemId,
    step: null,
  });
};

export const markChecklistItem =
  (itemId: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    markItem({ dispatch, getState }, itemId);
  };

startAppListening({
  matcher: isAnyOf(postProjectGroup.fulfilled, duplicateProjectGroup.fulfilled),
  effect: (_action, api) => markItem(api, "create-course"),
});

startAppListening({
  matcher: isAnyOf(duplicateProject.fulfilled),
  effect: (_action, api) => markItem(api, "create-module"),
});

startAppListening({
  matcher: generateFromPlanThunk.fulfilled.match,
  effect: (action, api) => {
    if (action.payload.planPhase === "generated" && action.payload.projectId != null) {
      markItem(api, "create-module");
    }
  },
});

startAppListening({
  matcher: isAnyOf(startPlanModeChat.fulfilled, continuePlanModeChat.fulfilled),
  effect: (_action, api) => {
    api.dispatch(tourSignal("scenaria-message-sent"));
  },
});

startAppListening({
  matcher: generateFromPlanThunk.pending.match,
  effect: (_action, api) => {
    api.dispatch(tourSignal("scenaria-generation-started"));
  },
});

startAppListening({
  matcher: generateFromPlanThunk.rejected.match,
  effect: (_action, api) => {
    api.dispatch(awaitStepInterrupted("create-module"));
  },
});

startAppListening({
  matcher: isAnyOf(createNewScene.fulfilled),
  effect: (_action, api) => {
    api.dispatch(tourSignal("graph-scene-created"));
  },
});

startAppListening({
  actionCreator: interactionCreatedLocally,
  effect: (_action, api) => {
    markItem(api, "customize-module");
    api.dispatch(tourSignal("graph-interaction-created"));
  },
});

startAppListening({
  actionCreator: setInteriorSceneStack,
  effect: (action, api) => {
    if (action.payload.length > 0) {
      markItem(api, "explore-scene-interior");
    }
  },
});

startAppListening({
  matcher: isAnyOf(askPublication.fulfilled),
  effect: (_action, api) => markItem(api, "request-publication"),
});

startAppListening({
  matcher: isAnyOf(inviteCreatorWithPerms.fulfilled, inviteExternalCreatorToOrg.fulfilled),
  effect: (_action, api) => {
    markItem(api, "manage-organization");
    markItem(api, "invite-teammate");
  },
});

startAppListening({
  matcher: setProjectTeam.fulfilled.match,
  effect: (action, api) => {
    if ((action.meta.arg.projectGroupId ?? -1) > 0) {
      markItem(api, "invite-teammate");
    }
  },
});

startAppListening({
  matcher: isAnyOf(
    inviteEnduserWithPerms.fulfilled,
    inviteExternalEnduserWithoutEmail.fulfilled,
    inviteExternalEnduser.fulfilled,
    inviteExternalEnduserWithoutProject.fulfilled,
    bulkInviteEndusersWithoutProject.fulfilled,
  ),
  effect: (_action, api) => {
    api.dispatch(tourSignal("enduser-invited"));
    markItem(api, "invite-teammate");
  },
});

const DEMO_CLEANUP_PROMPT_TOUR_IDS = ["creator-stats", "publication"];

startAppListening({
  actionCreator: applyOnboardingOps.fulfilled,
  effect: (action, api) => {
    if (action.payload.state.tours["enduser-stats"]?.status === "completed") {
      markItem(api, "take-stats-tour");
    }
    if (action.payload.state.tours["scene-interior-intro"]?.status === "completed") {
      markItem(api, "explore-scene-interior");
    }
    if (action.payload.state.tours["admin-team"]?.status === "completed") {
      markItem(api, "manage-organization");
    }
    if (action.payload.state.tours["creator-stats"]?.status === "completed") {
      markItem(api, "discover-stats");
    }
    const creditedChecklistItem = action.meta.arg.some((op) => op.op === "checklistItemDone");
    if (creditedChecklistItem) {
      const state = api.getState();
      const progress = getChecklistProgress(action.payload.state, selectOnboardingAudience(state));
      if (progress?.allDone && !selectSuggestionsMuted(state)) {
        api.dispatch(openChecklistCompletePrompt());
      }
    }
    const completedDemoTour = DEMO_CLEANUP_PROMPT_TOUR_IDS.some((tourId) =>
      action.meta.arg.some(
        (op) => op.op === "tourProgress" && op.id === tourId && op.status === "completed",
      ),
    );
    const allDemoToursDone = DEMO_CLEANUP_PROMPT_TOUR_IDS.every((tourId) => {
      const status = action.payload.state.tours[tourId]?.status;
      return status === "completed" || status === "skipped";
    });
    if (completedDemoTour && allDemoToursDone) {
      const state = api.getState();
      const hasDemoBuild = state.builds.all.some((build) => build.is_demo === true);
      if (selectHasDemoSessions(state) || hasDemoBuild) {
        api.dispatch(openDemoCleanupPrompt());
      }
    }
  },
});

export default onboardingListener;
